import {
  applyVariant,
  ConfirmDialog,
  forgetVariant,
  keepVariant,
  searchPlaces,
  formatMoney,
  formatRange,
  type Plan,
  type Variant,
  type Variants as VariantsScreen,
} from '@trippath/shared';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { day } from '../../lib/format';
import { routeWays } from '../search/PlanSearch';
import { useReadOnly } from '../../app/readOnly';

interface Props {
  plan: Plan;
  variants: VariantsScreen;
  /** Opens what was typed in the plan's search, to finish a place not found. */
  onDraft: (typed: string) => void;
  /** The variant shown on the map. */
  /** The variants shown on the map. */
  picked: readonly string[];
  /** A variant pressed: shown, or put away. */
  onPick: (id: string) => void;
  onChanged: (variants: VariantsScreen) => void;
  onApplied: (plan: Plan) => void;
  onFailed: () => void;
}

/** A variant by its name, or by its places. */
export function variantName(variant: Variant): string {
  const route = variant.places.length > 0 ? variant.places.map((p) => p.city) : variant.codes;
  return variant.name ?? route.join(' → ');
}

/**
 * Asks before a variant goes into the plan, saying what leaves it with its bookings; puts it in
 * once asked.
 */
export function UseVariant({
  trip,
  variant,
  onCancel,
  onUsed,
  onFailed,
}: {
  trip: string;
  variant: Variant;
  onCancel: () => void;
  onUsed: (plan: Plan) => void;
  onFailed: () => void;
}) {
  const { t } = useTranslation();
  return (
    <ConfirmDialog
      open
      title={t('variants.useQuestion', { name: variantName(variant) })}
      body={
        variant.dropped.length === 0
          ? t('variants.useBody')
          : t('variants.useBodyDrops', {
              cities: variant.dropped.map((d) => d.city).join(', '),
              count: variant.dropped.reduce((sum, d) => sum + d.bookings, 0),
            })
      }
      confirmLabel={t('variants.use')}
      onCancel={onCancel}
      onConfirm={() => {
        applyVariant(trip, variant.id).then(onUsed, onFailed);
      }}
    />
  );
}

/** `€91 less`, `€20 more` or `No change`, with its tone: what a variant does to the total. */
export function useChange(currency: string) {
  const { t, i18n } = useTranslation();
  return (minor: number) => {
    const amount = formatMoney({ currency, minor: Math.abs(minor) }, i18n.language);
    if (minor < 0) return { text: t('whatIf.less', { amount }), tone: 'saves' };
    if (minor > 0) return { text: t('whatIf.more', { amount }), tone: 'costs' };
    return { text: t('whatIf.same'), tone: 'same' };
  };
}

/** What a variant does to the plan, in words. */
export function useWhat() {
  const { t } = useTranslation();
  return (variant: Variant) => {
    const cities = variant.dropped.map((d) => d.city).join(', ');
    if (variant.places.length === 0) return t('variants.unfit');
    if (variant.from === null) return t('variants.elsewhere');
    if (variant.rejoin) {
      return cities === ''
        ? t('variants.otherWay', { city: variant.rejoin.city })
        : t('variants.skips', { cities });
    }
    return cities === ''
      ? t('variants.goesOn', { city: variant.from.city })
      : t('variants.instead', { cities });
  };
}

/**
 * The plan's variants: as branches, and each priced as the trip would be with it beside the
 * plan as it is; one is shown on the map, put into the plan or forgotten.
 */
export function Variants({
  plan,
  variants,
  picked,
  onPick,
  onChanged,
  onApplied,
  onFailed,
  onDraft,
}: Props) {
  const { t, i18n } = useTranslation();
  // A trip read only: its variants compared, none added, used or let go.
  const locked = useReadOnly();
  const lang = i18n.language;
  const ids = useId();
  const what = useWhat();
  const change = useChange(plan.trip.currency);
  const [using, setUsing] = useState<Variant | null>(null);
  const [typed, setTyped] = useState('');
  const [unread, setUnread] = useState<string | null>(null);
  // A link or a route typed is kept at once; one with a place not found goes to the search.
  const add = (event: { preventDefault: () => void }) => {
    event.preventDefault();
    const text = typed.trim();
    if (text === '') return;
    setUnread(null);
    searchPlaces(text, new AbortController().signal)
      .then((route) => {
        if (route.legs.length === 0) {
          setUnread(text);
          return;
        }
        const ways = routeWays(route, text);
        return keepVariant(
          plan.trip.id,
          ways.codes,
          ways.modes,
          ways.nights,
          ways.link,
          null,
        ).then((next) => {
          setTyped('');
          onChanged(next);
          // The one added is shown.
          next.variants.slice(-1).forEach((v) => {
            onPick(v.id);
          });
        });
      })
      .catch(onFailed);
  };
  const length = (days: number, end: string | null) =>
    end === null
      ? t('trips.days', { count: days })
      : t('variants.days', { count: days, end: day(end, lang) });

  return (
    <section className="panel variants" aria-labelledby={`${ids}-title`}>
      <h2 id={`${ids}-title`}>{t('variants.title')}</h2>
      {!locked && (
        <form className="variant-add" onSubmit={add}>
          <label className="visually-hidden" htmlFor={`${ids}-add`}>
            {t('variants.addLabel')}
          </label>
          <input
            id={`${ids}-add`}
            className="text-input"
            type="text"
            autoComplete="off"
            spellCheck={false}
            placeholder={t('variants.addHint')}
            value={typed}
            onChange={(event) => {
              setTyped(event.target.value);
            }}
          />
          <button type="submit" className="button button-primary">
            {t('variants.add')}
          </button>
        </form>
      )}
      {unread !== null && (
        <p className="variant-unread" role="status">
          {t('variants.unread')}{' '}
          <button
            type="button"
            className="link-button"
            onClick={() => {
              onDraft(unread);
            }}
          >
            {t('variants.fix')}
          </button>
        </p>
      )}
      {variants.variants.length === 0 ? (
        <p className="quiet">{t('variants.none')}</p>
      ) : (
        <div className="variants-body">
          <ul className="variant-cards">
            <li className="variant-card variant-plan">
              <p className="variant-name">{t('variants.asIs')}</p>
              <p className="mono variant-total">
                {formatMoney(variants.totals.expected, lang)}
              </p>
              <p className="quiet">
                {formatRange(variants.totals.total, lang, true)} ·{' '}
                {length(variants.totals.days, variants.end)}
              </p>
              {variants.totals.over && (
                <p className="variant-over">
                  {t('variants.over', { amount: formatMoney(variants.totals.over, lang) })}
                </p>
              )}
            </li>
            {variants.variants.map((variant) => {
              const delta =
                variant.delta === null ? null : change(variant.delta.expected.minor);
              const over = variant.totals ? variant.totals.over : null;
              return (
                <li key={variant.id} className="variant-card">
                  <button
                    type="button"
                    className="variant-pick"
                    aria-pressed={picked.includes(variant.id)}
                    onClick={() => {
                      onPick(variant.id);
                    }}
                  >
                    <span className="variant-name">{variantName(variant)}</span>{' '}
                    <span className="quiet">{what(variant)}</span>{' '}
                    {variant.totals && delta && (
                      <>
                        <span className={`variant-delta delta-${delta.tone}`}>
                          {delta.text}
                        </span>{' '}
                        <span className="mono variant-total">
                          {formatMoney(variant.totals.expected, lang)}
                        </span>{' '}
                        <span className="quiet">
                          {formatRange(variant.totals.total, lang, true)} ·{' '}
                          {length(variant.totals.days, variant.end)}
                        </span>{' '}
                      </>
                    )}
                    {over && (
                      <span className="variant-over">
                        {t('variants.over', { amount: formatMoney(over, lang) })}
                      </span>
                    )}
                  </button>
                  {!locked && (
                    <div className="variant-actions">
                      {variant.totals && (
                        <button
                          type="button"
                          className="button button-small button-primary"
                          onClick={() => {
                            setUsing(variant);
                          }}
                        >
                          {t('variants.use')}
                        </button>
                      )}
                      <button
                        type="button"
                        className="button button-small"
                        aria-label={t('variants.forget', { name: variantName(variant) })}
                        onClick={() => {
                          forgetVariant(plan.trip.id, variant.id).then((next) => {
                            onChanged(next);
                          }, onFailed);
                        }}
                      >
                        {t('variants.forgetShort')}
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      <p className="variant-precise">
        <a href={`/trips/${encodeURIComponent(plan.trip.id)}/routes`}>
          {t('variants.precise')}
        </a>
      </p>
      {using && (
        <UseVariant
          trip={plan.trip.id}
          variant={using}
          onCancel={() => {
            setUsing(null);
          }}
          onUsed={(next) => {
            setUsing(null);
            onApplied(next);
          }}
          onFailed={() => {
            setUsing(null);
            onFailed();
          }}
        />
      )}
    </section>
  );
}
